import { Component, ComponentFactoryResolver, Input, ViewChild, ViewContainerRef } from '@angular/core';
import { BaseInjectableComponent } from '@base/component/BaseInjectableComponent';
import { SysGlobals } from '@core/framework/sysglobals';
import { Utils } from '@core/helpers/Utils';
import { PageLayoutComponent } from '@ui/layouts/layout/page-layout/pagelayout.component';
import { DxValidationGroupComponent } from 'devextreme-angular';
import validationEngine from 'devextreme/ui/validation_engine';
import { Observable, Subject, Subscription, of, mergeMap } from 'rxjs';
import { ContextMenuElement, ToolbarSettings, ViewModeElement } from '../classes/BaseClasses';
import { EnumApplicationMode, EnumFilterPanelActions, EnumNotificationDeliveryOptions, EnumPageDisplayModes, EnumPageEntitySources } from '@core/framework/sysenums.enum';
import { BaseEntity, BaseEntityResponse, BaseResponse, BaseState, FileView, HistoryChangesSet, SharingInformation, RenderedTemplate } from '../entities/BaseEntities';
import { BaseComponent } from './BaseComponent';
import { ServiceBagService } from './ServiceBag';
import { AppInjector } from '@m4u-angular/core/injector/injector.module';

@Component({
    selector: 'base-page-component',
    template: '<div></div>'
})
export abstract class BasePageComponent<TEntity extends BaseEntity, TState extends BaseState> extends BaseComponent<TState> {
    //Entity bindings
    @Input() public Entity: TEntity = {} as TEntity;
    public EntityOriginal: TEntity = {} as TEntity;

    //Flags
    public IsLoading: boolean;
    private _isDirtyEntity: boolean = false;
    private _isDirtyCustom: boolean = false;
    public OnIsDirtyDisabled() {
        return false;
    }

    //Primary links
    public ToolbarSettings: ToolbarSettings;

    //Major fields
    public QueryParams: any;
    public ContextMenuDefault: ContextMenuElement[] = [];
    protected EntityTypes: string[];

    //Subscriptions
    private onScreenSizeChanged$Subscription: Subscription;
    private onFilterPanel$Subscription: Subscription;
    private onEntityChange$Subscription: Subscription;
    private pageHistory$Subscription: Subscription;

    //Overrides
    protected OnInitEntity() { }
    protected OnInitTitle(): string {
        return undefined;
    }
    protected OnInitSubtitle(): string {
        return undefined;
    }
    protected OnInitContextMenus(): ContextMenuElement[] {
        return undefined;
    }
    protected OnInitViewModes(): ViewModeElement[] {
        return undefined;
    }
    protected OnInitDefaultViewModeID(): any {
        return undefined;
    }
    public OnClose() {
        return true;
    }
    protected OnSubmitPage() { }
    protected OnApplyFilter() { }
    protected OnClearFilter() { }
    protected OnReadyFilter() { }
    protected OnGetContextMenuTitle(): string {
        return '';
    }
    public OnGetFilterDescription(): string {
        return '';
    }
    protected OnGetPrintTitle(): string {
        return '';
    }
    public OnInitInjectableComponent(injectableComponent: BaseInjectableComponent<BaseEntity, BaseState>) { }
    public OnGetHistoryCustom(): Observable<HistoryChangesSet[]> {
        return of(undefined);
    }
    public OnGetHistoryInternal(): Observable<HistoryChangesSet[]> {
        return of(undefined);
    }


    private _showAsWindowOnMobile: boolean;
    get ShowAsWindowOnMobile() {
        return this._showAsWindowOnMobile || this.OnShowAsWindowOnMobile();
    }
    protected OnShowAsWindowOnMobile() {
        return false;
    }

    @ViewChild(DxValidationGroupComponent) protected validationGroup: DxValidationGroupComponent;
    @ViewChild(PageLayoutComponent) protected layout: PageLayoutComponent;

    constructor
        (
            public override A: ServiceBagService,
            public override ComponentName: string,
            public EntityType: (new () => TEntity),
            StateType: (new () => TState),
            public DisplayMode: EnumPageDisplayModes = EnumPageDisplayModes.FullScreen,
            public EntityMode: EnumPageEntitySources = EnumPageEntitySources.Internal
        ) {
        super(A, ComponentName, StateType);

        if (this.A.Screen.IsMobile() && DisplayMode == EnumPageDisplayModes.Window && !this.ShowAsWindowOnMobile) {
            this.DisplayMode = EnumPageDisplayModes.BottomSheet;
        }
        this.BindHandler('NavigateBack');
    }

    public get IsDirty(): boolean {
        return !this.OnIsDirtyDisabled() && (this._isDirtyEntity || this._isDirtyCustom);
    }

    public SetDirty() {
        this._isDirtyCustom = true;
    }

    //Run initialization cycle
    protected override RunInitStartCycle(bFromAngularEvent: boolean) {
        if (this.IsRunCycle(bFromAngularEvent)) super.RunInitStartCycle(bFromAngularEvent);
    }
    protected override RunInitFinishCycle(bFromAngularEvent: boolean) {
        if (this.IsRunCycle(bFromAngularEvent)) super.RunInitFinishCycle(bFromAngularEvent);
    }
    protected override RunDestroyCycle(bFromAngularEvent: boolean) {
        if (this.IsRunCycle(bFromAngularEvent)) super.RunDestroyCycle(bFromAngularEvent);
    }
    private IsRunCycle(bFromAngularEvent: boolean) {
        return !(bFromAngularEvent && this.DisplayMode == EnumPageDisplayModes.Dialog);
    }

    //Initialize page
    override OnInitComponentInternal() {
        super.OnInitComponentInternal();
        this.InitEntity();

        // check that it is redundant
        /*this.onScreenSizeChanged$Subscription = this.A.Screen.ScreenSize$
            .subscribe(() => {
                this.ReloadToolbar();
            });*/

        this.pageHistory$Subscription = this.A.RouteHistoryService.PageHistory$.subscribe((historyItem) => {
            if (this instanceof historyItem.Component) {
                historyItem.Title = this.GetTitle();
                historyItem.ComponentName = this.ComponentName;
                this.A.RouteHistoryService.UpdateHistoryItem(historyItem);
            }
        });

        this.onFilterPanel$Subscription = this.A.SysEvents.FilterPanel
            .subscribe(action => {
                switch (action) {
                    case EnumFilterPanelActions.Apply:
                        this.OnApplyFilter();
                        break;
                    case EnumFilterPanelActions.Clear:
                        this.OnClearFilter();
                        break;
                    case EnumFilterPanelActions.Ready:
                        this.OnReadyFilter();
                        break;
                }
            });

        if (this.A.Screen.IsMobile() && this.DisplayMode == EnumPageDisplayModes.BottomSheet && !this.ShowAsWindowOnMobile) {
            this.m_IsDialogVisible = this.m_IsDialogRendered = true;
        }
    }

    override OnDestroyComponentInternal() {
        this.onScreenSizeChanged$Subscription && this.onScreenSizeChanged$Subscription.unsubscribe();
        this.onFilterPanel$Subscription && this.onFilterPanel$Subscription.unsubscribe();
        this.onEntityChange$Subscription && this.onEntityChange$Subscription.unsubscribe();
        this.pageHistory$Subscription && this.pageHistory$Subscription.unsubscribe();
    }

    override OnStartInitCycleInternal() {
        //Query params are not supported for dialog and bottom sheet
        if (this.DisplayMode == EnumPageDisplayModes.Dialog) return;
        //Get renamed query params
        this.QueryParams = this.A.Navigator.GetQueryParams(this.A.Navigator.IsCurrentRouteWindow());

        if (this.A.Screen.IsMobile() && this.QueryParams && this.DisplayMode === EnumPageDisplayModes.BottomSheet && this.QueryParams.ShowAsWindowOnMobile) {
            this.DisplayMode = EnumPageDisplayModes.Window;
            this._showAsWindowOnMobile = true;
        }
    }

    override OnInitComponentFinishedInternal() {
        this.InitToolbarSettings();
        this.NotifyReloadTitle();

        //if (this.PageLayout) {
        //    const containerRef = this.PageLayout.containerRef;
        //    const componentFactoryResolver = AppInjector.get(ComponentFactoryResolver);

        //    SysGlobals.InjectableComponentsOptions.forEach(x => {
        //        if (x.TargetModuleName === this.ModuleName && x.TargetComponentName === this.ComponentName) {
        //            // Resolve an injectable component
        //            const componentFactory = componentFactoryResolver.resolveComponentFactory(x.ComponentType);
        //            const componentRef = containerRef.createComponent<BaseInjectableComponent<BaseEntity, BaseState>>(componentFactory);
        //            // Set fields
        //            componentRef.instance.TargetComponentName = this.ComponentName;
        //            componentRef.instance.TargetAppName = this.ModuleName;
        //            componentRef.instance.EntityType = this.EntityType;

        //            // Handle the injectable component on high levels
        //            this.OnInitInjectableComponent(componentRef.instance);

        //            if (this.A.Screen.IsDesktop()) {
        //                // Add buttons to toolbar
        //                const buttonSettings = {
        //                    Type: 'normal',
        //                    Text: x.ButtonText,
        //                    Click: () => { componentRef.instance.BaseAction(); }
        //                };
        //                this.PageLayout.ToolbarButtons.push(buttonSettings);

        //                // Give buttons settings to ths injectable component
        //                componentRef.instance.ButtonSettings = buttonSettings;
        //            } else {
        //                this.ContextMenuDefault.push({
        //                    text: x.ButtonText,
        //                    onClick: () => { componentRef.instance.BaseAction(); }
        //                });
        //            }

        //        }
        //    });
        //}
    }

    override OnInitComponentFinishedAfterDetectInternal() {
        if (this.DisplayMode != EnumPageDisplayModes.Dialog) return;

        //Reset the validation
        this.ResetValidation();
    }

    public RefreshEntityOriginal() {
        this.EntityOriginal = Utils.MapObject<TEntity>(this.EntityType, this.Entity);
        this._isDirtyEntity = false;
        this._isDirtyCustom = false;
    }

    //Initialize an empty entity
    protected InitEntity() {
        if (this.EntityMode == EnumPageEntitySources.External) return;
        this.Entity = Utils.GetNewInstance<TEntity>(this.EntityType);
        SysGlobals.CurrentEntity = this.Entity;
        this.OnInitEntity();
        this.RefreshEntityOriginal();
    }

    //Apply a new entity to the page
    protected SetEntity(type: (new () => TEntity), serviceResponse: Observable<BaseEntityResponse<TEntity>>, bShowSuccessMessage: boolean = false, after?: (response: BaseEntityResponse<TEntity>) => void, fail?: (response: BaseEntityResponse<TEntity>) => void) {
        this.Do(serviceResponse, bShowSuccessMessage, (response: BaseEntityResponse<TEntity>) => {
            //Set entities
            this.Entity = Utils.MapObject<TEntity>(type, response.Entity);
            SysGlobals.CurrentEntity = this.Entity;

            //Save original (after event on purpose, to allow comparing with previous state)
            this.RefreshEntityOriginal();

            //subscribe on entity changes
            this.SubscribeOnEntityChanges();

            //Post-load event
            if (after != undefined) after(response);
        }, fail);
    }

    protected SubscribeOnEntityChanges() {
        if (this.OnIsDirtyDisabled()) return;
        const changed = new Subject<void>();

        //add proxy for emiting changes
        this.Entity = Utils.DeepProxy(this.Entity, changed) as TEntity;

        //add subscription for handling changes
        this.onEntityChange$Subscription && this.onEntityChange$Subscription.unsubscribe();
        this.onEntityChange$Subscription = changed.subscribe((_) => {
            this._isDirtyEntity = !Utils.IsEquals(this.Entity, this.EntityOriginal, true);
        });
    }

    //Return default title for the page
    //If defined using menu path, use it. 
    //If not, fallback to component name
    protected GetDefaultTitle(): string {
        let sTitle: string;
        if (this.DisplayMode != EnumPageDisplayModes.Dialog) sTitle = this.RC(`$$Pages.${SysGlobals.MenuPath}`);
        if (sTitle == undefined) sTitle = this.RC(`Pages.${this.ComponentStateName}`);
        return sTitle;
    }

    //Return the title for the component
    public GetTitle(): string {
        const sCustomTitle: string = this.OnInitTitle();
        const sFinalTitle: string = sCustomTitle ? sCustomTitle : this.GetDefaultTitle();
        return sFinalTitle;
    }

    public GetSubtitle(): string {
        const customSubtitle = this.OnInitSubtitle();
        return customSubtitle || '';
    }

    //Return context menu to the toolbar
    public GetContextMenus(): ContextMenuElement[] {
        let contextMenu = this.OnInitContextMenus();
        if (contextMenu === undefined) {
            contextMenu = [];
        }

        contextMenu = this.ContextMenuDefault.concat(contextMenu);

        return contextMenu;
    }


    //Return context menu to the toolbar
    public GetViewModes(): ViewModeElement[] {
        return this.OnInitViewModes();
    }

    public GetDefaultViewModeID(): any {

        return this.OnInitDefaultViewModeID();
    }

    //Signal toolbar to reload the title
    protected NotifyReloadTitle() {
        this.SetToolbarTitle();
        this.SetToolbarButtonsVisibility();

        this.A.SysEvents.ReloadToolbar(this.ToolbarSettings);
    }

    protected RepaintToolbar() {
        this.SetToolbarTitle();
        this.SetToolbarButtonsVisibility();

        this.A.SysEvents.ReloadToolbar(this.ToolbarSettings);
    }

    protected ReloadToolbar() {
        this.InitToolbarSettings();

        this.A.SysEvents.ReloadToolbar(this.ToolbarSettings);
    }

    InitToolbarSettings() {
        this.ToolbarSettings = new ToolbarSettings;
        this.ToolbarSettings.BackButtonClick = this.NavigateBack;
        this.ToolbarSettings.PageComponentName = this.ComponentName;
        this.ToolbarSettings.Page = this;
        this.ToolbarSettings.OnGetContextMenuItems = () => this.GetContextMenus();
        this.ToolbarSettings.ContextMenuTitle = this.OnGetContextMenuTitle();
        this.ToolbarSettings.ViewModes = this.GetViewModes();
        this.ToolbarSettings.CloseButtonClick = () => this.ClosePage();
        this.ToolbarSettings.OnShareButtonClick = (e) => {
            this.A.ShareService.OpenShareDialog(this.ComponentName, this.EntityOriginal?.TypeName, this.OnGetRenderedTemplate() , this.OnShare());
        };

        this.SetToolbarTitle();
        this.SetToolbarButtonsVisibility();

        const customToolbarSettings = this.OnGetToolbarSettings();
        if (customToolbarSettings) this.ToolbarSettings = { ...this.ToolbarSettings, ...customToolbarSettings };
    }

    protected OnShare(): (sharingInformation: SharingInformation) => Observable<BaseEntityResponse<FileView>> {
        return null;
    }

    protected OnGetRenderedTemplate(): (notificationTemplateID: number, deliveryOption: EnumNotificationDeliveryOptions) => Observable<BaseEntityResponse<RenderedTemplate>> {
        return this.A.ShareService.GetDefaultOnGetRenderedTemplate();
    }

    protected SetToolbarTitle() {
        if (this.ToolbarSettings == undefined) return;
        this.ToolbarSettings.Title = this.GetTitle();
        this.ToolbarSettings.Subtitle = this.GetSubtitle();

        this.A.RouteHistoryService.SetHistoryItemTitle(this.ComponentName, this.ToolbarSettings.Title);
    }

    protected SetToolbarButtonsVisibility() {
        if (this.ToolbarSettings == undefined) return;

        const customToolbarSettings = this.OnGetToolbarSettings();
        this.ToolbarSettings.ShowMenuButton = customToolbarSettings?.ShowMenuButton != null
            ? customToolbarSettings.ShowMenuButton
            : this.A.Screen.IsMobile() && this.A.AppConfigService.AppConfig.Mode === EnumApplicationMode.Standard && this.DisplayMode == EnumPageDisplayModes.FullScreen;
        this.ToolbarSettings.ShowBackButton = customToolbarSettings?.ShowBackButton != null
            ? customToolbarSettings.ShowBackButton
            : !this.A.AppConfigService.AppConfig.ShowBreadcrumb() && (
                (this.IsShowAsFullScreen() && this.A.RouteHistoryService.HistoryLength > 1) ||
                (this.DisplayMode == EnumPageDisplayModes.Window && this.A.RouteHistoryService.HasHistoryForWindow())
            );
        this.ToolbarSettings.ShowCloseButton = customToolbarSettings?.ShowCloseButton != null
            ? customToolbarSettings.ShowCloseButton
            : !this.IsShowAsFullScreen() && (this.DisplayMode == EnumPageDisplayModes.Window || this.DisplayMode == EnumPageDisplayModes.Dialog)
            || this.DisplayMode == EnumPageDisplayModes.BottomSheet;
        this.ToolbarSettings.IsAllowNotifications = customToolbarSettings?.IsAllowNotifications != null
            ? customToolbarSettings.IsAllowNotifications
            : this.A.AppConfigService.AppConfig.IsAllowNotifications && this.DisplayMode == EnumPageDisplayModes.FullScreen;
    }

    protected OnGetToolbarSettings() {
        return undefined;
    }

    protected override DoValidatorReset(): boolean {
        return !this.Entity || !this.Entity.EntryID;
    }

    //*IsDialogRendered is to prevent *ngIf from removing the content and causes unexplainable errors
    private m_IsDialogRendered: boolean;
    public get IsDialogRendered(): boolean {
        return this.m_IsDialogRendered;
    }

    //This is an indication for the template whether it is visible at the moment
    private m_IsDialogVisible: boolean;
    public get IsDialogVisible(): boolean {
        return this.m_IsDialogVisible;
    }

    private _showDialogAsBottomSheet: boolean;
    public get ShowDialogAsBottomSheet(): boolean {
        return this._showDialogAsBottomSheet;
    }

    //Must be executed to initialize the dialog, before setting any values to it
    //The reason we have this is that because the same dialog might be reused many times
    public InitDialog(InitialEntity?: TEntity) {
        if (InitialEntity != undefined) this.Entity = InitialEntity;
        this.RunInitStartCycle(false);
        this.RunInitFinishCycle(false);
    }

    //Show the component as a dialog
    private m_DialogCompletedHandler: (e: any) => void;
    private m_DialogCanceledHandler: () => void;
    private ShowDialog(DialogCompletedHandler: (e: any) => void, DialogCanceledHandler?: () => void) {
        //Check
        if (this.DisplayMode != EnumPageDisplayModes.Dialog && this.DisplayMode != EnumPageDisplayModes.BottomSheet)
            throw new Error('Your page is not a dialog, use navigation methods instead');

        //Reload title since it may be dependant on state that was loaded so far
        this.NotifyReloadTitle();

        //Set visibility flags and handler
        this.m_IsDialogVisible = true;
        this.m_IsDialogRendered = true;
        this.m_DialogCompletedHandler = DialogCompletedHandler;
        this.m_DialogCanceledHandler = DialogCanceledHandler;
    }

    //Combine both methods for cases when no initial state data has to be set
    public ShowDialogShort(
        DialogCompletedHandler: (e: any) => void,
        DialogCanceledHandler?: () => void,
        showAsUsualOnMobile?: boolean,
        initialEntity?: TEntity | any) {
        if (this.A.Screen.IsMobile() && !showAsUsualOnMobile) {
            this._showDialogAsBottomSheet = true;
        }

        this.InitDialog(initialEntity);
        this.ShowDialog(DialogCompletedHandler, DialogCanceledHandler);
    }

    public ShowBottomSheet(
        DialogCompletedHandler: (e: any) => void,
        DialogCanceledHandler?: () => void,
        initialEntity?: TEntity | any) {

        this._showDialogAsBottomSheet = true;
        this.InitDialog(initialEntity);
        this.ShowDialog(DialogCompletedHandler, DialogCanceledHandler);
    }

    //Close dialog and raise the callback to parent
    public CloseDialogWithSuccess(e?: any) {
        this.HideDialog();
        if (this.m_DialogCompletedHandler) this.m_DialogCompletedHandler(e);
    }

    //Close dialog without any event
    public CloseDialogWithCancel() {
        this.HideDialog();
        if (this.m_DialogCanceledHandler) this.m_DialogCanceledHandler();
    }

    //Hide the dialog
    private HideDialog() {
        this.State = Utils.GetNewInstance<TState>(this.StateType);

        //if (this.validationGroup && this.validationGroup.instance) {
        //    const result = validationEngine.validateGroup(this.validationGroup.instance);
        //    result.isValid && this.validationGroup.instance.reset();
        //}

        this.m_IsDialogVisible = false;
        this.RunDestroyCycle(false);
    }

    //public ScrollToTop() {
    //    this.PageLayout.scroll.instance.scrollTo(0);
    //}

    public IsShowAsFullScreen(): boolean {
        //Screen and current page sizes
        const bMobile: boolean = this.A.Screen.IsMobile();

        //No need for special classes if showing full screen, unless this is a mobile device
        if (this.DisplayMode == EnumPageDisplayModes.FullScreen) return true;

        //For window - show full screen or in window according to screen size. Use window dimensions
        if (this.DisplayMode == EnumPageDisplayModes.Window) return bMobile ? true : false;

        //For dialog - show always in partial view
        if (this.DisplayMode == EnumPageDisplayModes.Dialog) return false;//bMobile ? true : false;
    }

    //Closes page, whether dialog or window
    public ClosePage() {
        if (this.OnClose()) {
            switch (this.DisplayMode) {
                case EnumPageDisplayModes.Window:
                    this.A.RouteHistoryService.CloseWindow();
                    return;
                case EnumPageDisplayModes.Dialog:
                    this.CloseDialogWithCancel();
                    return;
                case EnumPageDisplayModes.BottomSheet:
                    if (this._showDialogAsBottomSheet) {
                        this._showDialogAsBottomSheet = false;
                        this.CloseDialogWithCancel();
                    } else {
                        this.A.RouteHistoryService.CloseWindow();
                    }
                    return;
            }
        }
    }

    GetFirstModuleName() {
        const moduleNames = this.A.AppConfigService.AppConfig.ModuleName.split(',');
        return moduleNames[0];
    }

    public get ClientHeight(): number {
        return this.GetLastPageBodyElement()?.getBoundingClientRect()?.height || 0;
    }

    public get ClientWidth(): number {
        return this.GetLastPageBodyElement()?.getBoundingClientRect()?.width || 0;
    }

    private GetLastPageBodyElement() {
        const pageBodyElements = document.querySelectorAll(`.${this.ComponentName} .page-body`);
        return pageBodyElements && pageBodyElements.length > 0 ? pageBodyElements[pageBodyElements.length - 1] : null;
    }

    public ShowPreview(pdfData: string | Blob, fileName: string) {
        this.A.SysEvents.SetPagePreviewState(this.ComponentName, true, pdfData, fileName);
    }

    public HidePreview() {
        this.A.SysEvents.SetPagePreviewState(this.ComponentName, false);
    }

    public GetHistory(): Observable<HistoryChangesSet[]> {
        return this.OnGetHistoryCustom().pipe(
            mergeMap(h => {
                if (!h) {
                    return this.OnGetHistoryInternal();
                }
                return of(h);
            }));
    }
}
